import React, { FormEvent, useContext, useEffect, useState } from "react";
import styled from "styled-components";
import HeaderOne from "../parts/HeaderOne";
import TextField from "../parts/TextField";
import { GetProfile200Response } from "../../../api/v1";
import { api } from "../../../api";
import { SessionContext } from "../layouts/UserLayout";
import ButtonSubmit from "../parts/ButtonSubmit";
import Alert from "../parts/Alert";
import FileField from "../parts/FileField";
import FormGroup from "../parts/FormGroup";
import Divider from "../parts/Divider";
import goldBadge from "../../assets/images/badges/gold.png";
import silverBadge from "../../assets/images/badges/silver.png";
import bronzeBadge from "../../assets/images/badges/bronze.png";

const badgesImage = {
  gold: goldBadge,
  silver: silverBadge,
  bronze: bronzeBadge,
};

const ProfileFormStyle = styled(FormGroup)`
  background: none;
  text-align: center;
  max-width: 600px;
  margin: 0 auto 10px;
`;

const HeaderOneStyle = styled(HeaderOne)`
  margin-bottom: 20px;
`;

const BadgesStyle = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;

  img {
    width: 100px;
  }
`;

const ImageUploadStyle = styled.div`
  display: flex;
  column-gap: 20px;
  text-align: left;
  margin-bottom: 10px;
  width: 100%;
`;

const ImageStyle = styled.div`
  position: relative;
  display: inline-block;

  button {
    position: absolute;
    background: #393432;
    border: none;
    border-radius: 25px;
    color: white;
    cursor: pointer;
    font-size: 12px;
    width: 25px;
    height: 25px;
    top: -10px;
    right: -10px;
    padding: 0;
  }

  img {
    max-width: 300px;
    height: 170px;
  }
`;

const UpdateButtonStyle = styled(ButtonSubmit)`
  max-width: 250px;
  width: 100%;
`;

const ProfileForm: React.FC = () => {
  const session = useContext(SessionContext);
  const [username, setUsername] =
    useState<GetProfile200Response["username"]>("");
  const [email, setEmail] = useState<GetProfile200Response["email"]>("");
  const [name, setName] = useState<GetProfile200Response["name"]>("");
  const [birthday, setBirthday] =
    useState<GetProfile200Response["birthday"]>("");
  const [password, setPassword] = useState<string>("");
  const [image, setImage] = useState<{ id: number; url: string }>();
  const [badges, setBadges] = useState<GetProfile200Response["badges"]>([]);
  const [message, setMessage] = useState(null);
  const [messageLevel, setMessageLevel] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!session.userId) return;
    api.getProfile().then((response) => {
      const user = response.data;
      setUsername(user.username);
      setEmail(user.email);
      setName(user.name || ""); // NOTE: nameがnullの場合があるので空文字をセット
      setBirthday(user.birthday || ""); // NOTE: birthdayがnullの場合があるので空文字をセット
      setBadges(user.badges);
      if (user.image) setImage({ id: user.image.id, url: user.image.url });
    });
  }, []);

  const onClickDeleteImageButton = () => {
    setImage(null);
  };

  const handleSubmit = async (event: FormEvent) => {
    setSubmitting(true);
    event.preventDefault();
    const params = {
      user: {
        email,
        name,
        birthday,
        password,
        image,
      },
    };
    await api
      .updateProfile(params)
      .then((response) => {
        setMessage(response.data.message);
        setMessageLevel("success");
      })
      .catch((error) => {
        setMessage(error.response.data.message);
        setMessageLevel("error");
      });

    setSubmitting(false);

    setTimeout(() => {
      setMessage(null);
    }, 5000);
  };

  return (
    <ProfileFormStyle onSubmit={(event) => handleSubmit(event)}>
      {badges.length > 0 && (
        <>
          <HeaderOneStyle>獲得バッチ</HeaderOneStyle>
          <BadgesStyle>
            {badges.map((badge, index) => {
              return (
                <img
                  key={index}
                  src={badgesImage[badge.type]}
                  alt={`${badge}-badge`}
                />
              );
            })}
          </BadgesStyle>
          <Divider mt="40px" mb="20px" />
        </>
      )}
      <HeaderOneStyle>プロフィール</HeaderOneStyle>
      {image && (
        <ImageUploadStyle>
          <ImageStyle>
            <button type="button" onClick={onClickDeleteImageButton}>
              ×
            </button>
            <img src={image.url} alt="店舗画像" />
          </ImageStyle>
        </ImageUploadStyle>
      )}
      <FileField
        id="profile-image"
        placeholder="画像"
        mb="20px"
        setFile={setImage}
      />
      <TextField
        type="text"
        id="username"
        name="username"
        label="ユーザーID※変更できません"
        mb="20px"
        value={username}
        required={true}
        readonly={true}
      />
      <TextField
        type="text"
        id="email"
        name="email"
        label="メールアドレス"
        placeholder="メールアドレス"
        mb="20px"
        value={email}
        required={true}
        onChange={(event) => setEmail(event.target.value)}
      />
      <TextField
        type="text"
        id="name"
        name="name"
        label="氏名"
        placeholder="氏名"
        mb="20px"
        value={name}
        required={true}
        onChange={(event) => setName(event.target.value)}
      />
      <TextField
        type="date"
        id="birthday"
        name="birthday"
        label="生年月日"
        placeholder="生年月日"
        mb="20px"
        value={birthday}
        required={true}
        onChange={(event) => setBirthday(event.target.value)}
      />
      <TextField
        type="password"
        id="password"
        name="password"
        label="パスワード"
        placeholder="パスワード"
        mb="20px"
        value={password}
        required={true}
        onChange={(event) => setPassword(event.target.value)}
      />
      <UpdateButtonStyle
        value="送信"
        submitting={submitting}
        submittingMessage="送信中..."
      />
      {message && <Alert level={messageLevel}>{message}</Alert>}
    </ProfileFormStyle>
  );
};

export default ProfileForm;
